import { useQuery } from "@tanstack/react-query";
import { countryName, parsePlace, type GroupView, type PauseView, type SetupResponse } from "@joogoos/contracts";
import { api } from "../api.js";

/**
 * What every Customers screen says about a group, in one place (38, ADR 0068).
 *
 * The group is what a person opens now, so its name, its places and its state are said on the menu, the Groups list
 * and its own page — and three screens that word a state three ways is three screens somebody stops trusting.
 */

export const SETUP = ["customers", "setup"];

/** Set up's one answer, which carries every group: the menu, the list and a group's page all read it. */
export const useSetup = () => useQuery({ queryKey: SETUP, queryFn: () => api.customersSetup() });

/** The groups a person works with: every one but the retired. */
export const liveGroups = (d: SetupResponse | undefined): GroupView[] => (d?.groups ?? []).filter((g) => g.state !== "dropped");

/** The two letters drawn before a group's name: its first place's country, or a dot for a group with none yet. */
export const flagOf = (g: GroupView): string => parsePlace(g.countries[0] ?? "")?.country ?? "·";

/** Where a group looks, in words: a town by its own name, a country by its. */
export const placesOf = (g: GroupView): string =>
  g.countries.map(parsePlace).map((p) => p && (p.town || countryName(p.country))).filter(Boolean).join(" · ");

export type GroupState = "running" | "trying" | "paused" | "stopped itself" | "retired";

/**
 * A group's state, as a person would say it.
 *
 * `kept` and `trying` are the loop's words for whether the numbers have earned it a place; *paused* is the owner's,
 * and *stopped itself* is one of its cells crossing a line (`customer_pauses`), which wins because it wants a person.
 */
export function stateOf(g: GroupView, pauses: readonly PauseView[] = []): GroupState {
  if (g.state === "dropped") return "retired";
  if (g.paused) return "paused";
  if (pauses.some((p) => p.groupId === g.id)) return "stopped itself";
  return g.state === "kept" ? "running" : "trying";
}

export const STATE_TONE: Record<GroupState, string> = {
  running: "bg-good/15 text-good",
  trying: "bg-line-2 text-ink-2",
  paused: "bg-line-2 text-muted",
  "stopped itself": "bg-accent/10 text-accent",
  retired: "bg-line-2 text-faint",
};

/** A group's two letters, in the slot a screen's icon takes. */
export const Flag = ({ code }: { code: string }) => (
  <span className="min-w-[24px] h-[17px] px-1 rounded bg-line-2 text-ink-2 text-[9.5px] font-bold tracking-[.03em] grid place-items-center shrink-0">{code}</span>
);
